import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import Logo from "@/components/Logo";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useTranslation();

  const redirectTo = searchParams.get("redirect") || "/dashboard";

  if (user) {
    navigate(redirectTo);
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (isForgot) {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        toast({ variant: "destructive", title: "Error", description: error.message });
      } else {
        toast({ title: "Check your email", description: "We sent you a password reset link." });
        setIsForgot(false);
      }
    } else if (isLogin) {
      const { error } = await signIn(email, password);
      if (error) {
        toast({ variant: "destructive", title: t("auth.signInFailed"), description: error.message });
      } else {
        navigate(redirectTo);
      }
    } else {
      const { error } = await signUp(email, password, displayName);
      if (error) {
        toast({ variant: "destructive", title: t("auth.signUpFailed"), description: error.message });
      } else {
        toast({ title: t("auth.checkEmail"), description: t("auth.verificationSent") });
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <a href="/" className="flex justify-center mb-8">
          <Logo size="lg" />
        </a>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-foreground font-serif mb-2">
            {isForgot ? "Reset Password" : isLogin ? t("auth.welcomeBack") : t("auth.createAccount")}
          </h1>
          <p className="text-muted-foreground text-sm font-sans mb-6">
            {isLogin && !isForgot ? t("auth.signInManage") : isForgot ? "Enter your email to receive a reset link." : t("auth.startCreating")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && !isForgot && (
              <div>
                <Label htmlFor="name" className="font-sans">{t("auth.yourName")}</Label>
                <Input
                  id="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Jane Doe"
                  required={!isLogin}
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="font-sans">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1"
              />
            </div>
            {!isForgot && (
              <div>
                <Label htmlFor="password" className="font-sans">{t("auth.password")}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required={!isForgot}
                  minLength={6}
                  className="mt-1"
                />
              </div>
            )}

            {isLogin && !isForgot && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setIsForgot(true)}
                  className="text-xs text-primary hover:underline"
                >
                  {t("auth.forgotPassword", { defaultValue: "Forgot password?" })}
                </button>
              </div>
            )}

            <Button type="submit" className="w-full rounded-full font-semibold" size="lg" disabled={loading}>
              {loading
                ? t("auth.loading")
                : isForgot
                  ? "Send Reset Link"
                  : isLogin
                    ? t("auth.signIn")
                    : t("auth.signUp")}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground font-sans">
            {isForgot ? (
              <button
                onClick={() => setIsForgot(false)}
                className="text-primary font-semibold hover:underline"
              >
                Back to Sign In
              </button>
            ) : (
              <>
                {isLogin ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary font-semibold hover:underline"
                >
                  {isLogin ? t("auth.signUpLink") : t("auth.signInLink")}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
